// Generated by TaroBase SDK Generator: 2026-07-18T20:30:00.103Z
// Collection: events/$eventId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * A community drop-off drive event created by a pickup agent. Households contribute plastic to it via the contributions sub-collection. Public read for homepage active-drives counter.
 */

export interface EventsRequest {
  createdBy: AddressType;
  title: string;
  city: string;
  locationName: string;
  lat: string;
  lng: string;
  status: string;
  targetKg: number | IncrementOperation | TokenAmount;
  createdAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface EventsResponse {
  createdBy: string;
  title: string;
  city: string;
  locationName: string;
  lat: string;
  lng: string;
  status: string;
  targetKg: number;
  createdAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a Events operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildEvents(eventId: string, data: EventsRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `events/${eventId}`, document: doc };
}

/**
 * Authenticated users only. createdBy (the agent) must equal the caller (@user.address). status typically starts at 'active'; targetKg is a required Int; createdAt is required Unix seconds. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setEvents(eventId: string, data: EventsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`events/${eventId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting Events: ${formatError(error)}`);
    return false;
  }
}

export type EventsRequestUpdate = Partial<EventsRequest>;

/**
 * Build a Events update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateEvents(eventId: string, data: EventsRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `events/${eventId}`, document: doc };
}

/**
 * Only the drive creator (@data.createdBy == @user.address) may update the drive (e.g. mark it 'consolidated'). (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateEvents(eventId: string, data: EventsRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`events/${eventId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating Events: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read all drives and fields so the homepage can count active drives without authentication.
   (Get Single Item)
 */
export async function getEvents(eventId: string): Promise<EventsResponse | null> {
  try {
    const result = await get(`events/${eventId}`);
    return result as EventsResponse | null;
  } catch (error) {
    console.error(`Error getting Events: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single Events document. (
  Read Operation Details: Anyone can read all drives and fields so the homepage can count active drives without authentication.
  )
 */
export function subscribeEvents(callback: (data: EventsResponse | null) => void, eventId: string): Promise<() => Promise<void>> {
  return subscribe(`events/${eventId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as EventsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to Events at path '${`events/${eventId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many Events items from collection events
 
  Read Operation Details: Anyone can read all drives and fields so the homepage can count active drives without authentication.
  
 */
export async function getManyEvents(filter: string = ""): Promise<EventsResponse[]> {
  try {
    const results = await get("events", { prompt: filter });
    return Array.isArray(results) ? results as EventsResponse[] : [];
  } catch (error) {
    console.error(`Error getting Events collection at path 'events': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Events collection at events
 
  Read Operation Details: Anyone can read all drives and fields so the homepage can count active drives without authentication.
  
 */
export function subscribeManyEvents(callback: (data: EventsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("events", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as EventsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Events collection at path '${"events"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all Events items from collection events
 
  Read Operation Details: Anyone can read all drives and fields so the homepage can count active drives without authentication.
  
 */
export async function getAllEvents(filter: string = ""): Promise<EventsResponse[]> {
  try {
    const results = await get("events", { prompt: filter });
    return Array.isArray(results) ? results as EventsResponse[] : [];
  } catch (error) {
    console.error(`Error getting Events collection at path 'events': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Events collection at events
 
  Read Operation Details: Anyone can read all drives and fields so the homepage can count active drives without authentication.
  
 */
export function subscribeAllEvents(callback: (data: EventsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("events", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as EventsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Events collection at path '${"events"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count Events items in collection events.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countEvents(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("events", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting Events at path 'events': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on Events items in collection events.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateEvents(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("events", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating Events at path 'events': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Only the drive creator may delete the drive.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteEvents(eventId: string): Promise<boolean> {
  try {
    const succeeded = await set(`events/${eventId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting Events: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for Events for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteEvents(eventId: string): DocumentOperation {
  return { path: `events/${eventId}`, document: null };
}


/**
 * A household's individual drop-off contribution to a drive event. Sub-collection because a drive has many contributions (Poof has no array fields). Public read for aggregate kg counters.
 */

export interface EventsContributionsRequest {
  household: AddressType;
  householdName: string;
  kg: number | IncrementOperation | TokenAmount;
  plasticType: string;
  createdAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface EventsContributionsResponse {
  household: string;
  householdName: string;
  kg: number;
  plasticType: string;
  createdAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a EventsContributions operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildEventsContributions(eventId: string, contributionId: string, data: EventsContributionsRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `events/${eventId}/contributions/${contributionId}`, document: doc };
}

/**
 * Authenticated users only. The household field must equal the caller (@user.address) so a household logs only its own contribution. kg is a required Int; plasticType is one of PET|HDPE|nylon|sachet|mixed; createdAt is required Unix seconds. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setEventsContributions(eventId: string, contributionId: string, data: EventsContributionsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`events/${eventId}/contributions/${contributionId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting EventsContributions: ${formatError(error)}`);
    return false;
  }
}

export type EventsContributionsRequestUpdate = Partial<EventsContributionsRequest>;

/**
 * Build a EventsContributions update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateEventsContributions(eventId: string, contributionId: string, data: EventsContributionsRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `events/${eventId}/contributions/${contributionId}`, document: doc };
}

/**
 * Only the contributor (@data.household == @user.address) may update their contribution. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateEventsContributions(eventId: string, contributionId: string, data: EventsContributionsRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`events/${eventId}/contributions/${contributionId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating EventsContributions: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read all contributions and fields so the homepage can aggregate total kg recovered without authentication.
   (Get Single Item)
 */
export async function getEventsContributions(eventId: string, contributionId: string): Promise<EventsContributionsResponse | null> {
  try {
    const result = await get(`events/${eventId}/contributions/${contributionId}`);
    return result as EventsContributionsResponse | null;
  } catch (error) {
    console.error(`Error getting EventsContributions: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single EventsContributions document. (
  Read Operation Details: Anyone can read all contributions and fields so the homepage can aggregate total kg recovered without authentication.
  )
 */
export function subscribeEventsContributions(callback: (data: EventsContributionsResponse | null) => void, eventId: string, contributionId: string): Promise<() => Promise<void>> {
  return subscribe(`events/${eventId}/contributions/${contributionId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as EventsContributionsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to EventsContributions at path '${`events/${eventId}/contributions/${contributionId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many EventsContributions items from collection events/${eventId}/contributions
 
  Read Operation Details: Anyone can read all contributions and fields so the homepage can aggregate total kg recovered without authentication.
  
 */
export async function getManyEventsContributions(eventId: string, filter: string = ""): Promise<EventsContributionsResponse[]> {
  try {
    const results = await get(`events/${eventId}/contributions`, { prompt: filter });
    return Array.isArray(results) ? results as EventsContributionsResponse[] : [];
  } catch (error) {
    console.error(`Error getting EventsContributions collection at path 'events/${eventId}/contributions': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in EventsContributions collection at events/${eventId}/contributions
 
  Read Operation Details: Anyone can read all contributions and fields so the homepage can aggregate total kg recovered without authentication.
  
 */
export function subscribeManyEventsContributions(callback: (data: EventsContributionsResponse[]) => void, eventId: string, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe(`events/${eventId}/contributions`, {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as EventsContributionsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to EventsContributions collection at path '${`events/${eventId}/contributions`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all EventsContributions items from collection events/${eventId}/contributions
 
  Read Operation Details: Anyone can read all contributions and fields so the homepage can aggregate total kg recovered without authentication.
  
 */
export async function getAllEventsContributions(eventId: string, filter: string = ""): Promise<EventsContributionsResponse[]> {
  try {
    const results = await get(`events/*/contributions`, { prompt: filter });
    return Array.isArray(results) ? results as EventsContributionsResponse[] : [];
  } catch (error) {
    console.error(`Error getting EventsContributions collection at path 'events/${eventId}/contributions': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in EventsContributions collection at events/${eventId}/contributions
 
  Read Operation Details: Anyone can read all contributions and fields so the homepage can aggregate total kg recovered without authentication.
  
 */
export function subscribeAllEventsContributions(callback: (data: EventsContributionsResponse[]) => void, eventId: string, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe(`events/*/contributions`, {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as EventsContributionsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to EventsContributions collection at path '${`events/*/contributions`}': ${formatError(error)}`);
    }
  });
}

/**
 * Count EventsContributions items in collection events/${eventId}/contributions.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countEventsContributions(eventId: string, filter: string = ""): Promise<AggregateResult> {
  try {
    return await count(`events/${eventId}/contributions`, { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting EventsContributions at path 'events/${eventId}/contributions': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on EventsContributions items in collection events/${eventId}/contributions.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateEventsContributions(eventId: string, operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate(`events/${eventId}/contributions`, operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating EventsContributions at path 'events/${eventId}/contributions': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Only the contributor may delete their contribution.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteEventsContributions(eventId: string, contributionId: string): Promise<boolean> {
  try {
    const succeeded = await set(`events/${eventId}/contributions/${contributionId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting EventsContributions: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for EventsContributions for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteEventsContributions(eventId: string, contributionId: string): DocumentOperation {
  return { path: `events/${eventId}/contributions/${contributionId}`, document: null };
}

